/**
 * ĐƠN TỪ & YÊU CẦU — nhân viên gửi đề nghị cho quản lý xử lý.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SERVER NHẬN BA LOẠI. BẢN THIẾT KẾ VẼ NĂM.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `vps/api/app/routers/requests.py` khai cứng bằng Pydantic `Literal`:
 *
 *     type: Literal['bo_sung_gio', 'dinh_chinh', 'xac_nhan_cong_trinh']
 *
 * Bản thiết kế `fe/yeu-cau-don-tu.pdf` vẽ năm thẻ: xin nghỉ, vắng mặt, làm
 * thêm, checkin/out, đổi ca. Bốn loại có `ma_server: null` bên dưới **chưa có
 * bảng, chưa có endpoint, chưa có luồng duyệt**. Gửi lên sẽ nhận 422.
 *
 * ── Vì sao vẫn hiện chúng ra ──────────────────────────────────────────────
 * Vì bản thiết kế có, và chủ dự án đã quyết áp toàn bộ. Nhưng hiện ra KHÔNG
 * có nghĩa là để người dùng bấm vào rồi gặp lỗi 422 không hiểu gì — thẻ nào
 * `ma_server: null` thì bị vô hiệu hoá và mang nhãn "chưa dùng được", nói
 * thẳng là hệ thống chưa làm phần này.
 *
 * ── Việc cần làm khi backend bổ sung một loại ─────────────────────────────
 * Sửa `ma_server` của dòng tương ứng ở bảng dưới. Giao diện, biểu mẫu, lịch
 * sử đơn đều chạy ngay — không phải tìm trong sáu tệp JSX.
 *
 * Bốn loại đơn CÓ THẬT hiện nay đều bắt nguồn từ tình huống trong docs/03:
 *   · Bổ sung giờ công thiếu   → C9 quên chấm vào, C10 quên chấm ra
 *   · Đính chính bản ghi sai   → QĐ-05, sửa = thêm bản ghi đính chính
 *   · Đổi thiết bị             → C4, §2.7 (đi qua `/auth/bind-device`)
 *   · Xác nhận công trình mới  → D7 công trình chưa khai báo
 */

import { request } from '../api/client'
import type { BanGhiTrongNgay, YeuCauDaGui } from '../types'

export interface LoaiDon {
  /** Khoá nội bộ của giao diện — không bao giờ gửi lên server. */
  khoa: string
  nhan: string
  mo_ta: string
  /** Giá trị `type` server nhận. `null` = server CHƯA hỗ trợ loại này. */
  ma_server: string | null
  /** Đơn này cần khai giờ và chiều vào/ra. */
  can_gio: boolean
  /** Đơn này cần chọn một bản ghi có sẵn trong ngày. */
  can_ban_ghi: boolean
  /** Biểu tượng + màu nền, khớp bản thiết kế. */
  icon: 'dong-ho' | 'nguoi-dung' | 'sao' | 'trang' | 'doi-chieu'
  mau: string
}

export const LOAI_DON: LoaiDon[] = [
  {
    khoa: 'checkin_out',
    nhan: 'Đơn checkin/out',
    mo_ta: 'Bạn quên chấm công lúc đến hoặc lúc về.',
    ma_server: 'bo_sung_gio',
    can_gio: true,
    can_ban_ghi: false,
    icon: 'dong-ho',
    mau: 'var(--lam-nen)',
  },
  {
    khoa: 'dinh_chinh',
    nhan: 'Đơn đính chính',
    mo_ta: 'Giờ đã ghi không đúng, đề nghị sửa lại.',
    ma_server: 'dinh_chinh',
    can_gio: true,
    can_ban_ghi: true,
    icon: 'doi-chieu',
    mau: 'var(--chinh-nhat)',
  },
  {
    khoa: 'xac_nhan_cong_trinh',
    nhan: 'Xác nhận công trình',
    mo_ta: 'Bạn chấm công ở nơi chưa có trong hệ thống.',
    ma_server: 'xac_nhan_cong_trinh',
    can_gio: false,
    can_ban_ghi: false,
    icon: 'trang',
    mau: 'var(--tim-nen)',
  },
  {
    khoa: 'doi_thiet_bi',
    nhan: 'Đơn đổi điện thoại',
    mo_ta: 'Cần quản lý duyệt; sau khi đổi sẽ khoá đổi tiếp 90 ngày.',
    // Đi qua `/auth/bind-device`, KHÔNG qua `/requests`. Xem `guiDon()`.
    ma_server: 'doi_thiet_bi',
    can_gio: false,
    can_ban_ghi: false,
    icon: 'nguoi-dung',
    mau: 'var(--canh-bao-nen)',
  },
  {
    khoa: 'xin_nghi',
    nhan: 'Đơn xin nghỉ',
    mo_ta: 'Nghỉ nhiều ngày làm việc.',
    ma_server: null,
    can_gio: false,
    can_ban_ghi: false,
    icon: 'trang',
    mau: 'var(--loi-nen)',
  },
  {
    khoa: 'vang_mat',
    nhan: 'Đơn vắng mặt',
    mo_ta: 'Vắng một khoảng thời gian trong ca làm việc.',
    ma_server: null,
    can_gio: true,
    can_ban_ghi: false,
    icon: 'nguoi-dung',
    mau: 'var(--canh-bao-nen)',
  },
  {
    khoa: 'lam_them',
    nhan: 'Đơn làm thêm',
    mo_ta: 'Làm thêm ngoài giờ của ca.',
    ma_server: null,
    can_gio: true,
    can_ban_ghi: false,
    icon: 'sao',
    mau: 'var(--canh-bao-nen)',
  },
  {
    khoa: 'doi_ca',
    nhan: 'Đơn đổi ca',
    mo_ta: 'Đổi sang một ca khác ca đã được phân.',
    ma_server: null,
    can_gio: false,
    can_ban_ghi: false,
    icon: 'doi-chieu',
    mau: 'var(--chinh-nhat)',
  },
]

/** Những loại server thật sự nhận — dùng để lọc, không nhúng cứng ở nơi khác. */
export const LOAI_DUNG_DUOC = LOAI_DON.filter((l) => l.ma_server !== null)

/** Tên hiển thị cho giá trị `type` server trả về. */
export function nhanLoaiDon(maServer: string): string {
  return LOAI_DON.find((l) => l.ma_server === maServer)?.nhan ?? maServer
}

/* ═══════════════════════════════════════════════════════════════════════════
 * ĐỌC
 * ═══════════════════════════════════════════════════════════════════════════ */

export const layDonDaGui = () => request<{ items: YeuCauDaGui[] }>('/requests')

/** Bản ghi chấm công trong một ngày — để chọn khi làm đơn đính chính. */
export const layBanGhiTrongNgay = (workDate: string) =>
  request<{ items: BanGhiTrongNgay[] }>(
    `/requests/records?work_date=${encodeURIComponent(workDate)}`)

/* ═══════════════════════════════════════════════════════════════════════════
 * GỬI
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface DonGui {
  client_uuid: string
  ma_server: string
  work_date: string
  reason: string
  /** ISO 8601 KÈM múi giờ, ví dụ `2026-09-15T08:00:00+07:00`. */
  punch_at?: string | null
  direction?: 'in' | 'out' | null
  location_id?: number | null
  record_id?: number | null
}

export const guiDon = (d: DonGui) =>
  request<{ id: number; status: string }>('/requests', {
    method: 'POST',
    body: JSON.stringify({
      client_uuid: d.client_uuid,
      type: d.ma_server,
      work_date: d.work_date,
      reason: d.reason,
      punch_at: d.punch_at ?? null,
      direction: d.direction ?? null,
      location_id: d.location_id ?? null,
      record_id: d.record_id ?? null,
    }),
  })
